import React from 'react';
import { Box, Typography } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import { sbg } from '../../theme/sbgTokens';

export interface PageHeaderProps {
  /** Section or record type, rendered as an overline ("Attendance", "Employee"). */
  overline?: React.ReactNode;
  /** The page title: sentence case, the object people recognize. */
  title: React.ReactNode;
  /**
   * The scope line: whose data, which period, how fresh — e.g.
   * "All departments · Oct 1–7, 2026 · Updated 9:03 AM ET". Pass an array to
   * have the parts joined with middle dots.
   */
  meta?: React.ReactNode | React.ReactNode[];
  /** Up to three actions: secondary first, primary last. Overflow goes in a ⋯ menu. */
  actions?: React.ReactNode;
  /** Leading element beside the title (an avatar on a record page). */
  leading?: React.ReactNode;
  /** Rendered under the header row — usually Tabs. */
  children?: React.ReactNode;
  sx?: SxProps<Theme>;
}

/**
 * Page header (book §8.5, §11.7): overline, `heading-2` title (Compact),
 * a scope/period/freshness meta line, and actions on the right.
 */
const PageHeader: React.FC<PageHeaderProps> = ({ overline, title, meta, actions, leading, children, sx }) => {
  const metaParts = Array.isArray(meta) ? meta.filter((part) => part !== null && part !== undefined && part !== false && part !== '') : null;
  return (
    <Box component="header" sx={{ mb: 3, ...sx }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: { xs: 'stretch', sm: 'flex-end' },
          justifyContent: 'space-between',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0 }}>
          {leading}
          <Box sx={{ minWidth: 0 }}>
            {overline && (
              <Typography variant="overline" component="p" sx={{ color: sbg.text.secondary, display: 'block', mb: 0.5 }}>
                {overline}
              </Typography>
            )}
            <Typography variant="h2" component="h1" sx={{ color: sbg.text.primary }}>
              {title}
            </Typography>
            {meta && (
              <Typography
                component="div"
                variant="body1"
                sx={{ color: sbg.text.secondary, mt: 0.5, display: 'flex', flexWrap: 'wrap', columnGap: 1, rowGap: 0.25 }}
              >
                {metaParts
                  ? metaParts.map((part, index) => (
                      <React.Fragment key={index}>
                        {index > 0 && <Box component="span" aria-hidden="true" sx={{ color: sbg.border.strong }}>·</Box>}
                        <span>{part}</span>
                      </React.Fragment>
                    ))
                  : meta}
              </Typography>
            )}
          </Box>
        </Box>
        {actions && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', flexShrink: 0, justifyContent: { xs: 'flex-start', sm: 'flex-end' } }}>
            {actions}
          </Box>
        )}
      </Box>
      {children && <Box sx={{ mt: 2 }}>{children}</Box>}
    </Box>
  );
};

export default PageHeader;
